import { Button, Switch } from 'antd';
import { useState, useEffect } from 'react';
import './index.scss';
import posthog from 'posthog-js';

// 6 months in milliseconds (approximately)
const CONSENT_EXPIRY_PERIOD = 6 * 30 * 24 * 60 * 60 * 1000;

// Cookie categories
const COOKIE_CATEGORIES = {
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
  FUNCTIONAL: 'functional',
};

const COOKIE_DESCRIPTIONS = {
  [COOKIE_CATEGORIES.ANALYTICS]: {
    name: 'Cookies analytiques',
    description:
      "Ces cookies nous permettent de mesurer l'audience de notre site et d'analyser la navigation pour améliorer nos services.",
    details: {
      purpose: "Analyse d'audience et amélioration du site web",
      legalBasis: 'Consentement (Art. 6.1.a RGPD)',
      retention: '26 mois maximum',
      thirdParties: 'PostHog Inc. (États-Unis), Google LLC (États-Unis)',
      dataTypes:
        "Pages visitées, durée de visite, type d'appareil, localisation approximative",
      rights:
        'Vous pouvez vous opposer à tout moment via les paramètres cookies',
    },
    required: false,
  },
  [COOKIE_CATEGORIES.MARKETING]: {
    name: 'Cookies marketing et publicitaires',
    description:
      "Ces cookies permettent de vous proposer des contenus et publicités personnalisés en fonction de vos centres d'intérêt.",
    details: {
      purpose: 'Personnalisation publicitaire et marketing ciblé',
      legalBasis: 'Consentement (Art. 6.1.a RGPD)',
      retention: '13 mois maximum',
      thirdParties: 'Partenaires publicitaires, réseaux sociaux',
      dataTypes: 'Préférences, historique de navigation, profil publicitaire',
      rights: "Droit d'opposition et de suppression à tout moment",
    },
    required: false,
  },
  [COOKIE_CATEGORIES.FUNCTIONAL]: {
    name: 'Cookies fonctionnels',
    description:
      "Ces cookies permettent d'améliorer votre expérience utilisateur en mémorisant vos préférences et paramètres.",
    details: {
      purpose: "Amélioration de l'expérience utilisateur",
      legalBasis: 'Consentement (Art. 6.1.a RGPD)',
      retention: '12 mois maximum',
      thirdParties: 'Aucun transfert vers des tiers',
      dataTypes:
        "Préférences d'affichage, paramètres de langue, choix utilisateur",
      rights: 'Paramétrage et suppression disponibles à tout moment',
    },
    required: false,
  },
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(true);
  const [cookiePreferences, setCookiePreferences] = useState({
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false,
    [COOKIE_CATEGORIES.FUNCTIONAL]: false,
  });

  useEffect(() => {
    checkCookieConsent();
  }, []);

  const checkCookieConsent = () => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    const cookiePreferencesStored = localStorage.getItem('cookiePreferences');
    const consentTimestamp = localStorage.getItem('cookieConsentTimestamp');
    const currentTime = new Date().getTime();

    // If consent exists
    if (cookieConsent !== null) {
      // Check if consent has expired (older than 6 months)
      if (
        consentTimestamp &&
        currentTime - parseInt(consentTimestamp) > CONSENT_EXPIRY_PERIOD
      ) {
        // Consent expired, show banner again
        resetConsent();
        setIsVisible(true);
      } else {
        // Valid consent
        setHasInteracted(true);
        setShowIcon(false);

        // Load saved preferences
        if (cookiePreferencesStored) {
          const preferences = JSON.parse(cookiePreferencesStored);
          setCookiePreferences(preferences);
          applyPreferences(preferences);
        } else if (cookieConsent === 'accepted') {
          // Legacy: if old format, assume all accepted
          const allAccepted = {
            [COOKIE_CATEGORIES.ANALYTICS]: true,
            [COOKIE_CATEGORIES.MARKETING]: true,
            [COOKIE_CATEGORIES.FUNCTIONAL]: true,
          };
          setCookiePreferences(allAccepted);
          applyPreferences(allAccepted);
        }
      }
    } else {
      // No consent, show banner
      setIsVisible(true);
    }
  };

  const resetConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookiePreferences');
    localStorage.removeItem('cookieConsentTimestamp');
    setHasInteracted(false);
    setCookiePreferences({
      [COOKIE_CATEGORIES.ANALYTICS]: false,
      [COOKIE_CATEGORIES.MARKETING]: false,
      [COOKIE_CATEGORIES.FUNCTIONAL]: false,
    });
    disableAnalytics();
  };

  const applyPreferences = preferences => {
    if (preferences[COOKIE_CATEGORIES.ANALYTICS]) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }

    // Add logic for other cookie types as needed
    // if (preferences[COOKIE_CATEGORIES.MARKETING]) {
    //   enableMarketing();
    // }
    // if (preferences[COOKIE_CATEGORIES.FUNCTIONAL]) {
    //   enableFunctional();
    // }
  };

  const savePreferences = preferences => {
    const timestamp = new Date().getTime();
    localStorage.setItem('cookieConsent', 'granular');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentTimestamp', timestamp.toString());

    setCookiePreferences(preferences);
    applyPreferences(preferences);

    setIsVisible(false);
    setHasInteracted(true);
    setShowIcon(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      [COOKIE_CATEGORIES.ANALYTICS]: true,
      [COOKIE_CATEGORIES.MARKETING]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: true,
    };
    savePreferences(allAccepted);
  };

  const handleSavePreferences = () => {
    savePreferences(cookiePreferences);
  };

  const handlePreferenceChange = (category, value) => {
    setCookiePreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const handleShowDetails = () => {
    setShowDetails(true);
  };

  const handleBackToSimple = () => {
    setShowDetails(false);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  // Function to show cookie banner, can be called from outside (exported via a global function)
  const showConsentBanner = () => {
    setIsVisible(true);
    setShowIcon(true);
  };

  // Expose function to window object to allow calling from other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.showCookieConsentBanner = showConsentBanner;

      // Add a method to get the consent information for compliance purposes
      window.getCookieConsentLog = () => {
        const consent = localStorage.getItem('cookieConsent');
        const preferences = localStorage.getItem('cookiePreferences');
        const timestamp = localStorage.getItem('cookieConsentTimestamp');

        if (consent && timestamp) {
          const date = new Date(parseInt(timestamp));
          return {
            consent,
            preferences: preferences ? JSON.parse(preferences) : null,
            timestamp,
            formattedDate: date.toISOString(),
            isValid:
              new Date().getTime() - parseInt(timestamp) <=
              CONSENT_EXPIRY_PERIOD,
          };
        }

        return null;
      };
    }

    return () => {
      // Clean up when component unmounts
      if (typeof window !== 'undefined') {
        delete window.showCookieConsentBanner;
        delete window.getCookieConsentLog;
      }
    };
  }, []);

  const enableAnalytics = () => {
    if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
      // Enable PostHog with GDPR compliant settings
      posthog.opt_in_capturing();

      // Set user properties for compliance tracking
      posthog.capture('cookie_consent_granted', {
        consent_type: 'analytics',
        timestamp: new Date().toISOString(),
        legal_basis: 'consent',
      });

      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'denied', // Ensure ads are separate
          functionality_storage: 'denied',
          personalization_storage: 'denied',
        });
      }
    }
  };

  const disableAnalytics = () => {
    if (process.env.NODE_ENV !== 'development' && typeof window !== 'undefined') {
      // Properly opt out and clear PostHog data
      posthog.capture('cookie_consent_revoked', {
        consent_type: 'analytics',
        timestamp: new Date().toISOString(),
      });

      posthog.opt_out_capturing();
      posthog.reset(); // Clear stored data

      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          functionality_storage: 'denied',
          personalization_storage: 'denied',
        });
      }
    }
  };

  const openPrivacyPolicy = () => {
    window.open(
      'https://optivest.fr/mentions-legales-politique-protection-donnees-personnelles-gestion-cookies/',
      '_blank',
    );
  };

  return (
    <>
      {showIcon && (
        <div
          className={`cookie-icon ${
            hasInteracted ? 'cookie-icon--interacted' : ''
          }`}
          onClick={toggleVisibility}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M21.662 2.821C18.866.025 11.663-.252 5.124 5.422-.987 10.725-.89 17.107 3.87 20.613c4.443 3.272 10.542 3.802 15.191-1.256 5.648-6.144 5.399-13.74 2.601-16.536z"></path>
            <path
              fill="#FFF"
              d="M8.104 14.644a.567.567 0 01-.804 0h-.001l-2.53-2.529a.57.57 0 01.805-.807l2.128 2.127 6.186-6.185a.57.57 0 01.805.805l-6.589 6.589zm4.895-1.92a.546.546 0 01-.387-.93l4.047-4.047a.549.549 0 01.774 0 .549.549 0 010 .774l-4.046 4.047a.545.545 0 01-.388.156zm4.964 1.236l-1.593 1.591a.544.544 0 01-.773 0 .549.549 0 010-.774l1.594-1.594a.547.547 0 11.79.755l-.016.017-.002.005zm0-2.985l-3.085 3.084a.549.549 0 01-.774-.775l3.087-3.087a.549.549 0 01.774.775l-.002.003z"
            ></path>
          </svg>
        </div>
      )}

      {isVisible && (
        <div className="cookie-consent">
          <div className="cookie-consent__container">
            <div className="cookie-consent__content-wrapper">
              {!showDetails && (
                <div className="cookie-consent__text">
                  <h2>
                    Respect de votre
                    <br />
                    <strong>vie privée</strong>
                  </h2>
                  <p>
                    Nous utilisons des cookies pour améliorer votre expérience
                    sur notre site. Certains sont nécessaires au fonctionnement,
                    d'autres nécessitent votre consentement. Vous pouvez
                    accepter, refuser ou personnaliser vos choix à tout moment.
                  </p>
                  <p className="cookie-consent__legal-info">
                    <strong>Responsable de traitement :</strong> Optivest
                    <br />
                    <strong>Base légale :</strong> Consentement (Art. 6.1.a
                    RGPD)
                    <br />
                    <strong>Durée de conservation :</strong> 6 mois maximum
                  </p>
                </div>
              )}

              {!showDetails && (
                <div className="cookie-consent__image">
                  <svg
                    width="800"
                    height="800"
                    viewBox="0 0 128 128"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    role="img"
                    className="iconify iconify--noto"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <path
                      d="M15.99 35.76l-4.26.98s-5.91 9.12-7.2 18.82c-.81 6.09-.84 14.04-.28 18c.57 3.96 2.61 12.39 5.82 18.33c3.09 5.72 6.1 7.97 9.39 9.96c1.41.85 2.67 3.76 4.87 5.17c2.06 1.32 4.78 1.58 4.78 1.58s7.52 11.81 23.79 13.86c19.09 2.4 28.43-6.22 28.43-6.22s7.64-.02 14.85-3.11c8.09-3.47 12.41-10.69 12.41-10.69s10.13-8.72 13.34-19.16c2.58-8.38 2.65-24.31.67-31.81s-5.81-13.85-8.85-17.92c-2.42-3.24-29.44-17.6-32.7-18.88c-3.25-1.27-33.52-3.25-34.65-2.4c-1.13.86-30.41 23.49-30.41 23.49z"
                      fill="#ac5811"
                    ></path>
                    <radialGradient
                      id="IconifyId17ecdb2904d178eab6390"
                      cx="64.164"
                      cy="57.596"
                      r="53.202"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset=".507" stopColor="#f29f58"></stop>
                      <stop offset=".715" stopColor="#f09d56"></stop>
                      <stop offset=".826" stopColor="#eb9550"></stop>
                      <stop offset=".914" stopColor="#e18745"></stop>
                      <stop offset=".99" stopColor="#d47436"></stop>
                      <stop offset="1" stopColor="#d27133"></stop>
                    </radialGradient>
                    <path
                      d="M26.53 19.75c-7.05 5.74-10.7 8.74-14.36 15.92S9.69 52.38 10.35 55.9c.65 3.52 1.83 10.31 2.48 13.7c.65 3.4 2.38 11.46 7.17 16.45c6.71 6.99 12.79 9.4 12.79 9.4s4.93 7.64 16.84 12.01c15.66 5.74 22.71-2.61 28.71-4.83s13.18-2.35 19.32-6.66c6.13-4.31 12.92-9.53 17.36-20.49s3.52-15.79 3.65-18.53c.13-2.74-.26-15.92-5.35-24.01s-11.21-12.95-13.57-14.49C94.53 15.06 75.89 6.95 71.2 6.04c-4.7-.91-21.46.51-28.11 3.25S26.53 19.75 26.53 19.75z"
                      fill="url(#IconifyId17ecdb2904d178eab6390)"
                    ></path>
                    <path
                      d="M56.28 71.46c-1.11-.35-2.28-2-2.56-5.53c-.39-5.03 3.32-6.42 3.32-6.42s4.42-1.17 7.53-2.28c2.89-1.03 4.97-2.35 4.97-2.35s.28-5.39 2.07-6.98c1.8-1.59 4.97-1.45 8.5-1.04c3.52.41 9.19 2.76 8.36 4.9c-.83 2.14-1.8 4.28-5.18 6.22c-3.38 1.93-5.8 3.73-7.53 5.25c-1.73 1.53-4.9 3.45-10.15 5.39s-7.73 3.34-9.33 2.84z"
                      fill="#ffc86e"
                    ></path>
                    <path
                      d="M26.55 26.47c-3.32-.68-8.01 6.51-8.88 8.89c-.88 2.38-1.78 8.18-1.88 10.83c-.13 3.29-.19 6.38 1.19 8.89c1.06 1.94 3.63 3.03 5.94 1.03c2.31-2 6.32-7.48 7.38-8.25s4.16-1.79 2.94-6.83c-.5-2.06-5.57-6.32-5.82-6.9c-.24-.57 1.01-7.27-.87-7.66z"
                      fill="#ffc86e"
                    ></path>
                    <path
                      d="M50.02 17.44c.13 1.93 1.71 5.02 3.42 6.69c2.56 2.49 14.3 10.91 17.34 10.43s6.35-5.94 7.32-7.46s2.9-3.66 3.66-4.63c.76-.97 2.76-3.18 1.11-5.39c-1.66-2.21-5.8-3.66-8.56-3.87c-2.76-.21-7.05-2-10.71-1.86c-3.66.14-13.92 1.11-13.58 6.09z"
                      fill="#ffc86e"
                    ></path>
                  </svg>
                </div>
              )}

              {showDetails && (
                <div className="cookie-consent__preferences">
                  <div
                    className="cookie-consent__back-button"
                    onClick={handleBackToSimple}
                  >
                    ← Retour
                  </div>

                  <div className="cookie-consent__categories">
                    {Object.entries(COOKIE_DESCRIPTIONS).map(
                      ([category, info]) => (
                        <div
                          key={category}
                          className="cookie-consent__category"
                        >
                          <div className="cookie-consent__category-header">
                            <div className="cookie-consent__category-info">
                              <h4>{info.name}</h4>
                              <p>{info.description}</p>
                              {showDetailedInfo && (
                                <div className="cookie-consent__category-details">
                                  <div className="cookie-consent__detail-item">
                                    <strong>Finalité :</strong>{' '}
                                    {info.details.purpose}
                                  </div>
                                  <div className="cookie-consent__detail-item">
                                    <strong>Base légale :</strong>{' '}
                                    {info.details.legalBasis}
                                  </div>
                                  <div className="cookie-consent__detail-item">
                                    <strong>Durée de conservation :</strong>{' '}
                                    {info.details.retention}
                                  </div>
                                  <div className="cookie-consent__detail-item">
                                    <strong>Tiers destinataires :</strong>{' '}
                                    {info.details.thirdParties}
                                  </div>
                                  <div className="cookie-consent__detail-item">
                                    <strong>Données traitées :</strong>{' '}
                                    {info.details.dataTypes}
                                  </div>
                                  <div className="cookie-consent__detail-item">
                                    <strong>Vos droits :</strong>{' '}
                                    {info.details.rights}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Switch
                              checked={cookiePreferences[category]}
                              onChange={checked =>
                                handlePreferenceChange(category, checked)
                              }
                              disabled={info.required}
                            />
                          </div>
                        </div>
                      ),
                    )}

                    <div className="cookie-consent__detailed-info-toggle">
                      <button
                        onClick={() => setShowDetailedInfo(!showDetailedInfo)}
                        className="cookie-consent__info-toggle-btn"
                      >
                        {showDetailedInfo
                          ? '- Masquer les détails'
                          : '+ Afficher les détails légaux'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="cookie-consent__actions">
              {!showDetails ? (
                <>
                  <Button
                    className="cookie-consent__button cookie-consent__button--secondary"
                    onClick={handleShowDetails}
                  >
                    Personnaliser
                  </Button>
                  <Button
                    className="cookie-consent__button cookie-consent__button--primary"
                    onClick={handleAcceptAll}
                  >
                    Accepter tout
                  </Button>
                </>
              ) : (
                <Button
                  className="cookie-consent__button cookie-consent__button--primary"
                  onClick={handleSavePreferences}
                >
                  Sauvegarder mes préférences
                </Button>
              )}
            </div>

            <div className="cookie-consent__footer">
              <span
                className="cookie-consent__link"
                onClick={openPrivacyPolicy}
              >
                Politique de confidentialité
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
